import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Search, X } from "lucide-react";
import { ApprovalDecisionModal } from "@/components/businesses/ApprovalDecisionModal";
import { StatusBadge } from "@/components/businesses/StatusBadge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SafeImage } from "@/components/ui/SafeImage";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";
import { ChipGroup } from "@/components/ui/FilterPanel";
import { useAuth } from "@/contexts/AuthContext";
import {
  deriveConnectedServiceStatus,
  filterConnectedServices,
} from "@/lib/business-analytics";
import { useApproveConnectedService, useRejectConnectedService } from "@/lib/queries";
import { formatDate, getErrorMessage, imageSrc } from "@/lib/utils";
import type { Business, ConnectedService } from "@/types/database";

type Decision = "approve" | "reject" | "revoke";

type Props = {
  services: ConnectedService[];
  businesses: Business[];
};

export function ConnectedServicesExplorer({ services, businesses }: Props) {
  const { adminProfile, session } = useAuth();
  const approveMutation = useApproveConnectedService();
  const rejectMutation = useRejectConnectedService();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("pending");
  const [decision, setDecision] = useState<Decision | null>(null);
  const [selected, setSelected] = useState<ConnectedService | null>(null);
  const [error, setError] = useState<string | null>(null);

  const businessIdByBusinessID = useMemo(() => {
    const map = new Map<string, number>();
    for (const business of businesses) {
      if (business.businessID) map.set(business.businessID, business.id);
    }
    return map;
  }, [businesses]);

  const filtered = useMemo(
    () => filterConnectedServices(services, { search, status }),
    [services, search, status]
  );

  const pendingCount = services.filter(
    (service) => deriveConnectedServiceStatus(service) === "pending"
  ).length;

  const reviewer =
    adminProfile?.userID || session?.user.email || session?.user.id || "overwatch-admin";

  const loading = approveMutation.isPending || rejectMutation.isPending;

  function openDecision(service: ConnectedService, next: Decision) {
    setError(null);
    setSelected(service);
    setDecision(next);
  }

  async function handleConfirm(rejectionReason?: string) {
    if (!selected || !decision) return;
    setError(null);

    const businessNumericId = selected.businessID
      ? businessIdByBusinessID.get(selected.businessID)
      : undefined;

    try {
      if (decision === "approve") {
        await approveMutation.mutateAsync({
          id: selected.id,
          reviewedBy: reviewer,
          businessNumericId,
        });
      } else {
        await rejectMutation.mutateAsync({
          id: selected.id,
          reviewedBy: reviewer,
          rejectionReason: rejectionReason || "Revoked by Overwatch admin",
          businessNumericId,
        });
      }
      setDecision(null);
      setSelected(null);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update approval status."));
    }
  }

  return (
    <>
      <Workspace>
        <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-md flex-1">
              <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
              <Input
                placeholder="Business name, type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <ChipGroup
              label="Status"
              options={["Pending", "Approved", "Rejected"]}
              value={
                status === "pending"
                  ? "Pending"
                  : status === "approved"
                    ? "Approved"
                    : status === "rejected"
                      ? "Rejected"
                      : ""
              }
              onChange={(value) =>
                setStatus(
                  value === "Pending"
                    ? "pending"
                    : value === "Approved"
                      ? "approved"
                      : value === "Rejected"
                        ? "rejected"
                        : ""
                )
              }
            />
          </div>
          {pendingCount > 0 ? (
            <p className="mt-4 text-sm font-semibold text-muted">
              <span className="text-foreground">{pendingCount}</span> application
              {pendingCount === 1 ? "" : "s"} awaiting review
            </p>
          ) : null}
        </WorkspaceSection>

        <WorkspaceSection border className="p-4 sm:p-5">
          {error ? <p className="mb-4 text-sm font-semibold text-red-600">{error}</p> : null}

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="font-bold">No applications</p>
              <p className="mt-1 text-sm text-muted">
                {status === "pending"
                  ? "Nothing waiting for approval right now."
                  : "Adjust your filters to see results."}
              </p>
            </div>
          ) : (
            <div className="stagger-children space-y-3">
              {filtered.map((service) => {
                const derived = deriveConnectedServiceStatus(service);
                const logo = imageSrc(service.businessLogo);
                const businessNumericId = service.businessID
                  ? businessIdByBusinessID.get(service.businessID)
                  : undefined;

                return (
                  <div
                    key={service.id}
                    className="flex flex-col gap-4 rounded-3xl bg-canvas p-4 sm:flex-row sm:items-center sm:p-5"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      {logo ? (
                        <SafeImage
                          src={logo}
                          alt={service.businessName ?? "Business"}
                          width={48}
                          height={48}
                          className="h-12 w-12 rounded-2xl object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-subtle text-sm font-bold text-accent-foreground">
                          {(service.businessName ?? "S").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-extrabold tracking-tight">
                            {service.businessName || "Unknown business"}
                          </p>
                          <StatusBadge status={derived} />
                        </div>
                        <p className="mt-1 text-sm font-semibold text-muted">
                          {service.businessType || "Unspecified type"}
                        </p>
                        <p className="mt-1 text-xs text-muted">
                          Applied {formatDate(service.created_at)}
                          {service.reviewedAt ? ` · Reviewed ${formatDate(service.reviewedAt)}` : ""}
                        </p>
                        {service.rejectionReason ? (
                          <p className="mt-2 text-xs font-semibold text-orange-700">
                            Reason: {service.rejectionReason}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {businessNumericId != null ? (
                        <Link
                          to={`/businesses/${businessNumericId}`}
                          className="inline-flex h-9 items-center rounded-full bg-surface px-4 text-sm font-bold text-muted transition hover:text-foreground"
                        >
                          View business
                        </Link>
                      ) : null}

                      {derived === "pending" || derived === "rejected" ? (
                        <Button
                          size="sm"
                          variant="brand"
                          onClick={() => openDecision(service, "approve")}
                          disabled={loading}
                        >
                          <Check className="h-4 w-4" />
                          Approve
                        </Button>
                      ) : null}

                      {derived === "pending" ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => openDecision(service, "reject")}
                          disabled={loading}
                        >
                          <X className="h-4 w-4" />
                          Reject
                        </Button>
                      ) : null}

                      {derived === "approved" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openDecision(service, "revoke")}
                          disabled={loading}
                        >
                          Revoke
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </WorkspaceSection>
      </Workspace>

      <ApprovalDecisionModal
        open={Boolean(decision && selected)}
        decision={decision}
        service={selected}
        loading={loading}
        onClose={() => {
          if (loading) return;
          setDecision(null);
          setSelected(null);
        }}
        onConfirm={(reason) => void handleConfirm(reason)}
      />
    </>
  );
}
